import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";

// =============================================================================
// SINGLE DATE PICKER
// =============================================================================
// PURPOSE: Styled single-date picker matching DateRangePicker visual language.
// Used for "Closed after" filter on InstallmentTrackingPage.
//
// VISUAL: Glass pill trigger (calendar icon + date + chevron) → portal dropdown
// with single month calendar, prev/next nav, clear button.
// =============================================================================

interface SingleDatePickerProps {
  /** Optional inline label rendered before the trigger pill. Omit for a bare pill. */
  label?: string;
  value: string; // ISO date string "YYYY-MM-DD" or ""
  onChange: (value: string) => void;
  placeholder?: string;
  /** data-testid on the trigger button (for Playwright). */
  triggerTestId?: string;
  /** Full-width trigger (fills the rail column) instead of inline auto-width. */
  block?: boolean;
}

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

const DAY_NAMES = ["S", "M", "T", "W", "T", "F", "S"];

// Explicit dropdown box so a position:fixed element with no `left`/`width` can't
// resolve to full-viewport width (which dragged it off-screen inside the modal).
const DROPDOWN_WIDTH = 264;
const DROPDOWN_EST_HEIGHT = 340; // month nav + 6 day rows + footer + padding
const VIEWPORT_MARGIN = 8;

const SingleDatePicker: React.FC<SingleDatePickerProps> = ({
  label,
  value,
  onChange,
  placeholder = "Select date",
  triggerTestId,
  block = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Derive initial viewMonth from value. When value changes externally,
  // the month derived from value is used as initial state via the key trick below.
  const derivedMonth = useMemo(() => {
    if (value) {
      const d = new Date(value + "T00:00:00");
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  }, [value]);
  const [viewMonth, setViewMonth] = useState(derivedMonth);
  // Sync when derivedMonth changes (value prop changed externally)
  const derivedKey = derivedMonth.getTime();
  const [lastDerivedKey, setLastDerivedKey] = useState(derivedKey);
  if (derivedKey !== lastDerivedKey) {
    setLastDerivedKey(derivedKey);
    setViewMonth(derivedMonth);
  }
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  // Position the dropdown right-aligned to the trigger, then HARD-CLAMP both axes
  // into the viewport so it can never render off-screen — the failure mode when
  // this picker is used inside a tall/centered modal. Opens downward by default,
  // flips up when there's no room below, and a final clamp guarantees visibility.
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const M = VIEWPORT_MARGIN;

      // Vertical: below → flip up → clamp.
      let top = rect.bottom + M;
      if (top + DROPDOWN_EST_HEIGHT + M > window.innerHeight) {
        const up = rect.top - DROPDOWN_EST_HEIGHT - M;
        top = up >= M ? up : window.innerHeight - DROPDOWN_EST_HEIGHT - M;
      }
      top = Math.min(Math.max(M, top), Math.max(M, window.innerHeight - DROPDOWN_EST_HEIGHT - M));

      // Horizontal: right-align to trigger, clamp into viewport.
      let left = rect.right - DROPDOWN_WIDTH;
      left = Math.min(Math.max(M, left), window.innerWidth - DROPDOWN_WIDTH - M);

      setDropdownPos({ top, left });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const outsideContainer = containerRef.current && !containerRef.current.contains(target);
      const outsidePortal = portalRef.current && !portalRef.current.contains(target);
      if (outsideContainer && (!portalRef.current || outsidePortal)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Close on ESC
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKey);
      return () => document.removeEventListener("keydown", handleKey);
    }
  }, [isOpen]);

  // viewMonth sync handled above via derivedMonth + lastDerivedKey pattern

  const selectedDate = useMemo(() => {
    if (!value) return null;
    return new Date(value + "T00:00:00");
  }, [value]);

  const displayText = useMemo(() => {
    if (!selectedDate) return placeholder;
    return selectedDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }, [selectedDate, placeholder]);

  const handleDayClick = useCallback(
    (day: Date) => {
      const iso = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, "0")}-${String(day.getDate()).padStart(2, "0")}`;
      onChange(iso);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange("");
    setIsOpen(false);
  }, [onChange]);

  const navigateMonth = useCallback((dir: "prev" | "next") => {
    setViewMonth((prev) => {
      const d = new Date(prev);
      d.setMonth(d.getMonth() + (dir === "next" ? 1 : -1));
      return d;
    });
  }, []);

  // Render calendar grid
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = new Date();
  const monthLabel = viewMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const days: React.ReactNode[] = [];
  for (let i = 0; i < firstDay; i++) {
    days.push(<div key={`empty-${i}`} className="h-[28px]" />);
  }
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const isToday = isSameDay(date, today);
    const isSelected = selectedDate && isSameDay(date, selectedDate);

    days.push(
      <button
        key={d}
        type="button"
        onClick={() => handleDayClick(date)}
        className={`h-[28px] w-[28px] flex items-center justify-center token-calendar rounded-md cursor-pointer select-none transition-colors
          ${isSelected ? "bg-blue-500 text-white font-semibold" : "text-glass hover:bg-black/[0.06]"}
          ${isToday && !isSelected ? "font-bold ring-1 ring-blue-400" : ""}
        `}
      >
        {d}
      </button>
    );
  }

  const dropdown =
    isOpen &&
    dropdownPos &&
    createPortal(
      <div
        ref={portalRef}
        data-testid="single-date-portal"
        // SOLID white popover (not `liquid-glass`): a translucent glass calendar let the
        // modal's rail labels bleed through behind the grid. Matches TagDropdown's opaque
        // surface — same reason. `position: fixed` stays inline (it would also be needed if
        // this were ever glass again — css-glass gotcha #1: liquid-glass forces relative).
        className="z-[10000] rounded-xl shadow-2xl p-4 border border-black/[0.08]"
        style={{
          position: "fixed",
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: DROPDOWN_WIDTH,
          // Inline opaque white — guarantees the rail behind never bleeds through,
          // regardless of any class cascade. (`animate-fade-in-up` was a phantom
          // class with no keyframe, removed.)
          backgroundColor: "#ffffff",
        }}
      >
        {/* Month nav */}
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => navigateMonth("prev")}
            className="p-1 rounded hover:bg-black/[0.06] text-glass-secondary"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <span className="token-13 font-semibold text-glass">{monthLabel}</span>
          <button
            type="button"
            onClick={() => navigateMonth("next")}
            className="p-1 rounded hover:bg-black/[0.06] text-glass-secondary"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 gap-0 mb-1">
          {DAY_NAMES.map((name, i) => (
            <div
              key={i}
              className="h-[20px] flex items-center justify-center token-8 font-medium text-glass-muted"
              style={{ width: 28 }}
            >
              {name}
            </div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-0">{days}</div>

        {/* Footer: Clear + Today */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-black/[0.08]">
          <button
            type="button"
            onClick={handleClear}
            className="token-11 text-glass-muted hover:text-glass transition-colors anim-tap"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={() => {
              const t = new Date();
              handleDayClick(new Date(t.getFullYear(), t.getMonth(), t.getDate()));
            }}
            className="token-11 text-ds-accent hover:opacity-80 font-medium transition-colors anim-tap"
          >
            Today
          </button>
        </div>
      </div>,
      document.body
    );

  return (
    <div ref={containerRef} className={`flex items-center gap-2 ${block ? "w-full" : ""}`}>
      {label ? (
        <span className="token-11 text-glass-secondary whitespace-nowrap">{label}</span>
      ) : null}
      <button
        ref={buttonRef}
        type="button"
        data-testid={triggerTestId}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 bg-black/[0.04] border border-black/[0.08] rounded-lg px-2.5 py-1.5 token-13 transition-colors hover:bg-black/[0.07] anim-tap ${
          value ? "text-glass" : "text-glass-muted"
        } ${block ? "w-full justify-between" : ""}`}
      >
        <svg
          className="h-3.5 w-3.5 text-glass-muted flex-shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span className="whitespace-nowrap">{displayText}</span>
        {value && (
          // role="button" span, NOT <button> — a <button> nested in the trigger
          // <button> is invalid HTML (React warns). Span keeps it interactive + a11y.
          <span
            role="button"
            tabIndex={0}
            aria-label="Clear date"
            onClick={(e) => {
              e.stopPropagation();
              onChange("");
              setIsOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onChange("");
                setIsOpen(false);
              }
            }}
            className="text-glass-muted hover:text-glass ml-0.5 inline-flex cursor-pointer"
            title="Clear"
          >
            <svg
              className="h-3 w-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </span>
        )}
      </button>
      {dropdown}
    </div>
  );
};

export default SingleDatePicker;
